import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Col, Form, Row, Spinner, Table} from "react-bootstrap";
import Container from "react-bootstrap/Container";
import {DatatableWrapper, TableBody, TableColumnType, TableHeader} from "react-bs-datatable";
import {useParams} from "react-router-dom";
import notifier from "simple-react-notifications2";

import {useGetLeasePackageCatalog} from "../../api/catalogs/useLeasePackageCatalog";
import {useReplaceLeasePackageCatalogTenants} from "../../api/catalogs/useReplaceLeasePackageCatalogTenants";
import {useSaveLeasePackageCatalog} from "../../api/catalogs/useSaveLeasePackageCatalog";
import {useDisableImport, useEnableImport} from "../../api/imports/useDisableEnableImports";
import {useGetTenants} from "../../api/tenants/useTenants";
import CheckboxMultiSelect, {CheckboxMultiSelectOption} from "../../components/form/CheckboxMultiSelect";
import ConfirmModal from "../../components/modals/ConfirmModal";
import {
    LeasePackageCatalogDetailDto,
    LeasePackageCatalogImportSummaryDto,
    LeasePackageCatalogTenantDto,
} from "../../model/LeasePackageCatalog";
import {getRemovedIds, haveSameIds, normalizeIds} from "../../util/idSet";

type CatalogImportTableRow = LeasePackageCatalogImportSummaryDto & {
    button: React.ReactNode;
};

export default function CatalogDetailRoute() {
    const {catalogId} = useParams();
    return <CatalogDetail key={catalogId} catalogIdParam={catalogId}/>;
}

function CatalogDetail({catalogIdParam}: {catalogIdParam?: string}) {
    const catalogId = Number(catalogIdParam);
    const validCatalogId = Number.isSafeInteger(catalogId) && catalogId > 0;
    const {
        loading: loadingCatalog,
        error: catalogError,
        execute: executeCatalog,
    } = useGetLeasePackageCatalog();
    const {
        loading: loadingRename,
        error: renameError,
        execute: executeRename,
    } = useSaveLeasePackageCatalog();
    const {
        loading: loadingAssignment,
        execute: executeAssignment,
    } = useReplaceLeasePackageCatalogTenants();
    const {
        loading: loadingTenants,
        data: tenants,
        error: tenantsError,
        execute: executeTenants,
    } = useGetTenants();
    const {loading: loadingEnableImport, execute: executeEnableImport} = useEnableImport();
    const {loading: loadingDisableImport, execute: executeDisableImport} = useDisableImport();
    const [catalog, setCatalog] = useState<LeasePackageCatalogDetailDto>();
    const [renameName, setRenameName] = useState("");
    const [renameValidationError, setRenameValidationError] = useState<string>();
    const [canonicalTenantIds, setCanonicalTenantIds] = useState<number[]>([]);
    const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>([]);
    const [assignmentError, setAssignmentError] = useState<string>();
    const [assignmentInfo, setAssignmentInfo] = useState<string>();
    const [assignmentUncertain, setAssignmentUncertain] = useState(false);
    const [assignmentOperationPending, setAssignmentOperationPending] = useState(false);
    const [showRemovalConfirmation, setShowRemovalConfirmation] = useState(false);
    const [pendingTenantIds, setPendingTenantIds] = useState<number[]>();
    const [importActionError, setImportActionError] = useState<string>();
    const [pendingImportId, setPendingImportId] = useState<number>();
    const latestCatalogRead = useRef(0);

    useEffect(() => {
        setCatalog(undefined);
        setRenameName("");
        setCanonicalTenantIds([]);
        setSelectedTenantIds([]);
        setAssignmentError(undefined);
        setAssignmentInfo(undefined);
        setAssignmentUncertain(false);
        if(validCatalogId) loadCatalog(false).catch(() => undefined);
        // The route ID is the reload boundary; hook execute identities are unstable.
        return () => {
            latestCatalogRead.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogIdParam]);

    async function loadCatalog(preserveIntendedSelection: boolean) {
        const request = ++latestCatalogRead.current;
        const loadedCatalog = await executeCatalog(catalogId);
        if(request !== latestCatalogRead.current) return loadedCatalog;
        const loadedTenantIds = normalizeIds(loadedCatalog.tenants.map(tenant => tenant.id));
        setCatalog(loadedCatalog);
        setRenameName(loadedCatalog.name);
        setCanonicalTenantIds(loadedTenantIds);
        if(!preserveIntendedSelection) setSelectedTenantIds(loadedTenantIds);
        setAssignmentUncertain(false);
        return loadedCatalog;
    }

    function getTenantOptions(): CheckboxMultiSelectOption[] {
        const tenantOptions = new Map<number, CheckboxMultiSelectOption>();
        tenants.forEach(tenant => tenantOptions.set(tenant.id, {
            id: tenant.id,
            label: `${tenant.title} (ID ${tenant.id})`,
        }));
        catalog?.tenants.forEach(tenant => tenantOptions.set(tenant.id, {
            id: tenant.id,
            label: `${tenant.title} (ID ${tenant.id})`,
        }));
        return Array.from(tenantOptions.values()).sort((left, right) =>
            left.label.localeCompare(right.label) || left.id - right.id,
        );
    }

    function toggleTenant(id: number) {
        setSelectedTenantIds(current => normalizeIds(
            current.includes(id) ? current.filter(selectedId => selectedId !== id) : [...current, id],
        ));
    }

    async function renameCatalog(event: React.FormEvent) {
        event.preventDefault();
        const normalizedName = renameName.trim();
        if(normalizedName.length === 0) {
            setRenameValidationError("Name is required");
            return;
        }
        if(normalizedName.length > 255) {
            setRenameValidationError("Name must not exceed 255 characters");
            return;
        }
        setRenameValidationError(undefined);
        const updatedCatalog = await executeRename({name: normalizedName}, catalogId);
        setCatalog(current => current ? {...current, name: updatedCatalog.name} : updatedCatalog);
        setRenameName(updatedCatalog.name);
        notifier.success("Catalog renamed");
    }

    async function reconcileAssignment(intendedIds: number[]) {
        const request = ++latestCatalogRead.current;
        try {
            const refreshedCatalog = await executeCatalog(catalogId);
            if(request !== latestCatalogRead.current) return;
            const refreshedIds = normalizeIds(refreshedCatalog.tenants.map(tenant => tenant.id));
            setCatalog(refreshedCatalog);
            setCanonicalTenantIds(refreshedIds);
            setSelectedTenantIds(intendedIds);
            const assignmentMatches = haveSameIds(refreshedIds, intendedIds);
            setAssignmentUncertain(!assignmentMatches);
            if(assignmentMatches) {
                setAssignmentError(undefined);
                setAssignmentInfo("Refreshed server data matches the intended tenant selection.");
            } else {
                setAssignmentError("The save result is still unclear. Server data was refreshed and your intended selection was preserved. Retry the save or refetch before editing again.");
            }
        } catch (e) {
            if(request !== latestCatalogRead.current) return;
            setSelectedTenantIds(intendedIds);
            setAssignmentUncertain(true);
            setAssignmentError("The save and the follow-up refresh both failed. Retry the save or refetch before editing again.");
        }
    }

    async function saveTenantAssignment(intendedIds: number[]) {
        latestCatalogRead.current += 1;
        setAssignmentOperationPending(true);
        setAssignmentError(undefined);
        setAssignmentInfo(undefined);
        try {
            const canonicalTenants = await executeAssignment(catalogId, {tenantIds: intendedIds});
            const canonicalIds = normalizeIds(canonicalTenants.map(tenant => tenant.id));
            setCanonicalTenantIds(canonicalIds);
            setSelectedTenantIds(canonicalIds);
            setAssignmentUncertain(false);
            setCatalog(current => current ? {
                ...current,
                tenantsCount: canonicalTenants.length,
                tenants: canonicalTenants,
            } : current);
            notifier.success("Tenant assignments saved");
        } catch (e) {
            await reconcileAssignment(intendedIds);
        } finally {
            setAssignmentOperationPending(false);
            setShowRemovalConfirmation(false);
            setPendingTenantIds(undefined);
        }
    }

    function requestTenantAssignmentSave() {
        const intendedIds = normalizeIds(selectedTenantIds);
        if(getRemovedIds(canonicalTenantIds, intendedIds).length > 0) {
            setPendingTenantIds(intendedIds);
            setShowRemovalConfirmation(true);
        } else {
            saveTenantAssignment(intendedIds).catch(() => undefined);
        }
    }

    function getRemovedTenants(): LeasePackageCatalogTenantDto[] {
        const removedIds = new Set(getRemovedIds(canonicalTenantIds, pendingTenantIds || selectedTenantIds));
        return (catalog?.tenants || []).filter(tenant => removedIds.has(tenant.id));
    }

    async function toggleImport(leasePackageImport: LeasePackageCatalogImportSummaryDto) {
        setImportActionError(undefined);
        setPendingImportId(leasePackageImport.id);
        try {
            if(leasePackageImport.enabled) {
                await executeDisableImport(leasePackageImport.id);
            } else {
                await executeEnableImport(leasePackageImport.id);
            }
            await loadCatalog(false);
            notifier.success(`Import ${leasePackageImport.enabled ? "disabled" : "enabled"}`);
        } catch (e) {
            setImportActionError("The import status could not be updated or refreshed.");
        } finally {
            setPendingImportId(undefined);
        }
    }

    const loadingImportAction = loadingEnableImport || loadingDisableImport || pendingImportId !== undefined;
    const anySavePending = loadingRename || loadingAssignment || assignmentOperationPending || loadingCatalog || loadingImportAction;
    const assignmentDirty = !haveSameIds(canonicalTenantIds, selectedTenantIds);
    const renameDirty = !!catalog && renameName.trim() !== catalog.name;
    const importActionsDisabled = anySavePending || assignmentDirty || renameDirty || assignmentUncertain;

    const importsHeader: TableColumnType<CatalogImportTableRow>[] = [
        {title: "Id", prop: "id", isSortable: true},
        {
            title: "File",
            prop: "filename",
            isSortable: true,
            cell: row => <>{row.filename || "No filename"}</>,
        },
        {
            title: "Status",
            prop: "enabled",
            isSortable: true,
            cell: row => <>{row.enabled ? "Enabled" : "Disabled"}</>,
        },
        {
            title: "Created",
            prop: "created",
            isSortable: true,
            cell: row => <>{row.created.replace("T", " ")}</>,
        },
        {title: "Vehicles", prop: "vehicleCount", isSortable: true},
        {title: "Packages", prop: "leasePackageCount", isSortable: true},
        {
            prop: "button",
            cell: row => (
                <Button
                    variant={row.enabled ? "outline-danger" : "outline-success"}
                    size="sm"
                    disabled={importActionsDisabled}
                    aria-label={`${row.enabled ? "Disable" : "Enable"} import ${row.id}`}
                    title={assignmentDirty || renameDirty || assignmentUncertain
                        ? "Save or discard catalog changes before changing an import status."
                        : undefined}
                    onClick={() => toggleImport(row).catch(() => undefined)}
                >
                    {pendingImportId === row.id && <Spinner animation="border" size="sm" className="me-2" />}
                    {row.enabled ? "Disable" : "Enable"}
                </Button>
            ),
        },
    ];

    if(!validCatalogId) {
        return <Container><Alert variant="danger" className="mt-5">Invalid catalog ID.</Alert></Container>;
    }
    if(loadingCatalog && !catalog) {
        return (
            <Container>
                <p className="mt-5" role="status">
                    <Spinner animation="border" size="sm" className="me-2"/>Loading catalog...
                </p>
            </Container>
        );
    }
    if(!catalog) {
        return (
            <Container>
                <Alert variant="danger" className="mt-5" role="alert">
                    <p>{catalogError ? "The catalog could not be loaded." : "Catalog data is unavailable."}</p>
                    <Button variant="outline-danger" onClick={() => loadCatalog(false).catch(() => undefined)}>
                        Retry
                    </Button>
                </Alert>
            </Container>
        );
    }

    return (
        <Container>
            <Row className="mt-5 mb-4">
                <Col>
                    <h1>{catalog.name}</h1>
                    <p>Status: {catalog.active ? "Active" : "Inactive"}</p>
                </Col>
            </Row>

            <section className="mb-5" aria-labelledby="rename-catalog-heading">
                <h2 id="rename-catalog-heading" className="h4">Rename Catalog</h2>
                {(renameValidationError || renameError) && (
                    <Alert variant="danger" role="alert">
                        {renameValidationError || "The catalog could not be renamed."}
                    </Alert>
                )}
                <Form onSubmit={event => renameCatalog(event).catch(() => undefined)}>
                    <Form.Group controlId="rename-catalog-name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            value={renameName}
                            onChange={event => setRenameName(event.target.value)}
                            disabled={anySavePending}
                            isInvalid={!!renameValidationError}
                        />
                    </Form.Group>
                    <Button
                        className="mt-3"
                        variant="primary"
                        type="submit"
                        disabled={anySavePending || renameName.trim() === catalog.name}
                    >
                        {loadingRename && <Spinner animation="border" size="sm" className="me-2" />}
                        Save Name
                    </Button>
                </Form>
            </section>

            <section className="mb-5" aria-labelledby="catalog-imports-heading">
                <h2 id="catalog-imports-heading" className="h4">Imports</h2>
                <p>{catalog.importsCount} enabled import{catalog.importsCount === 1 ? "" : "s"} in this catalog.</p>
                {importActionError && <Alert variant="danger" role="alert">{importActionError}</Alert>}
                {catalog.imports.length === 0 ? (
                    <Alert variant="info">This catalog does not contain any imports.</Alert>
                ) : (
                    <DatatableWrapper body={catalog.imports as CatalogImportTableRow[]} headers={importsHeader}>
                        <Table responsive className="custom-table">
                            <TableHeader/>
                            <TableBody/>
                        </Table>
                    </DatatableWrapper>
                )}
            </section>

            <section className="mb-5" aria-labelledby="catalog-tenants-heading">
                <h2 id="catalog-tenants-heading" className="h4">Tenant Assignments</h2>
                <p>
                    {catalog.tenantsCount} tenant{catalog.tenantsCount === 1 ? "" : "s"} currently assigned.
                </p>
                {tenantsError && (
                    <Alert variant="danger" role="alert">
                        <p>Available tenants could not be loaded.</p>
                        <Button
                            variant="outline-danger"
                            onClick={() => executeTenants().catch(() => undefined)}
                            disabled={loadingTenants}
                        >
                            Retry
                        </Button>
                    </Alert>
                )}
                {assignmentError && <Alert variant="danger" role="alert">{assignmentError}</Alert>}
                {assignmentInfo && <Alert variant="info" role="status">{assignmentInfo}</Alert>}
                {loadingTenants ? (
                    <p aria-live="polite"><Spinner animation="border" size="sm" className="me-2" />Loading tenants...</p>
                ) : (
                    <CheckboxMultiSelect
                        idPrefix="catalog-tenant"
                        legend="Tenants with access to this catalog"
                        options={getTenantOptions()}
                        selectedIds={selectedTenantIds}
                        onToggle={toggleTenant}
                        disabled={anySavePending || !!tenantsError || assignmentUncertain}
                        emptyMessage="No tenants are available."
                    />
                )}
                <Button
                    className="mt-3"
                    variant="primary"
                    onClick={requestTenantAssignmentSave}
                    disabled={anySavePending || loadingTenants || !!tenantsError || assignmentUncertain || !assignmentDirty}
                >
                    {loadingAssignment && <Spinner animation="border" size="sm" className="me-2" />}
                    Save Tenant Assignments
                </Button>
                {assignmentUncertain && (
                    <>
                        <Button
                            className="mt-3 ms-2"
                            variant="outline-primary"
                            onClick={() => saveTenantAssignment(normalizeIds(selectedTenantIds)).catch(() => undefined)}
                            disabled={assignmentOperationPending || loadingAssignment || loadingCatalog || loadingRename}
                        >
                            Retry Save
                        </Button>
                        <Button
                            className="mt-3 ms-2"
                            variant="outline-primary"
                            onClick={() => {
                                setAssignmentOperationPending(true);
                                reconcileAssignment(normalizeIds(selectedTenantIds))
                                    .finally(() => setAssignmentOperationPending(false));
                            }}
                            disabled={assignmentOperationPending || loadingAssignment || loadingCatalog || loadingRename}
                        >
                            Retry Refetch
                        </Button>
                    </>
                )}
            </section>

            <ConfirmModal
                show={showRemovalConfirmation}
                pending={assignmentOperationPending}
                onHide={() => setShowRemovalConfirmation(false)}
                onConfirm={() => {
                    if(pendingTenantIds) saveTenantAssignment(pendingTenantIds).catch(() => undefined);
                }}
                title="Remove Tenant Access?"
                cancelLabel="Cancel"
                confirmLabel="Confirm"
                body={(
                    <>
                        <p>The following tenants will immediately lose access to this catalog's offers:</p>
                        <ul>
                            {getRemovedTenants().map(tenant => (
                                <li key={tenant.id}>{tenant.title} (ID {tenant.id})</li>
                            ))}
                        </ul>
                        <p>An unsigned order may be blocked at its next existing breaking-change check.</p>
                    </>
                )}
            />
        </Container>
    );
}
